/**
 * @voltxampere/web — Selection → clipboard fragment helper (Sprint 13).
 *
 * Walks a selected-id list and pulls the corresponding components,
 * wires (only if both endpoints connect to selected components),
 * and nets (those referenced by the surviving wires) into a
 * clipboard payload. Empty selection → null.
 *
 * The bbox is the AABB of the selected components — paste uses
 * its centre as the anchor.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit } from "@voltxampere/schema";
import type { ClipboardFragmentInput } from "@voltxampere/ui/canvas";

export function fragmentFromSelection(
  circuit: Circuit,
  selectedIds: readonly string[],
): ClipboardFragmentInput | null {
  if (selectedIds.length === 0) return null;
  const idSet = new Set(selectedIds);
  const components = circuit.components.filter((c) => idSet.has(c.id));
  if (components.length === 0) return null;
  const componentIdSet = new Set(components.map((c) => c.id));
  // Sprint 13 simplification: include selected wires AND any wire
  // whose net is referenced by a selected component's terminals.
  // The full "two-endpoint connectivity" check needs Sprint 14
  // net-merge logic; for Sprint 13 we just include any wire whose
  // id is selected.
  const wires = circuit.wires.filter((w) => idSet.has(w.id));
  const netIds = new Set<string>();
  for (const w of wires) netIds.add(w.netId);
  for (const c of components) {
    for (const t of c.terminals) netIds.add(t.netId);
  }
  void componentIdSet; // keep the variable for the Sprint 14 expansion
  const nets = circuit.nets.filter((n) => netIds.has(n.id));
  const bbox = boundingBoxOf(components.map((c) => c.position));
  return { components, wires, nets, bbox };
}

interface Pt {
  readonly x: number;
  readonly y: number;
}

function boundingBoxOf(points: readonly Pt[]): { x: number; y: number; w: number; h: number } {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
